import { Fragment } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationType, OrganizationRole } from "@bookdental/schemas";
import { avisoDeEscopoOculto, gruposPorEscopo } from "@/components/permissions/permission-scopes";
import { trpc } from "@/lib/trpc";
import { ROLE_LABEL } from "@/components/team/role-meta";
import { CustomRolesSection } from "@/components/permissions/CustomRolesSection";
import { ehDonoOuSocio } from "@bookdental/schemas";

// ─── Seção "Permissões" (matriz customizável por papel — só o OWNER edita) ─

const PERMISSION_ROLE_COLUMNS: Exclude<OrganizationRole, "OWNER" | "PARTNER">[] = [
  "VIEWER",
  "RECEPTIONIST",
  "ASSISTANT",
  "DENTIST",
  "ADMIN",
];

export function PermissoesSecao() {
  const utils = trpc.useUtils();
  const meQuery = trpc.me.current.useQuery();
  const activeOrganization = meQuery.data?.activeOrganization;
  const isOwner = ehDonoOuSocio(activeOrganization?.role);

  const permissionsQuery = trpc.organization.permissions.list.useQuery(undefined, { enabled: isOwner });

  const setPermission = trpc.organization.permissions.set.useMutation({
    onSuccess: () => {
      utils.organization.permissions.list.invalidate();
      toast.success("Permissão atualizada.");
    },
    onError: (err) => toast.error(err.message || "Erro ao atualizar permissão."),
  });

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Só o proprietário pode configurar permissões
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          Fale com o proprietário atual da organização se precisar ajustar o que cada papel pode
          acessar.
        </p>
      </div>
    );
  }

  const rows = permissionsQuery.data ?? [];
  const isGranted = (role: OrganizationRole, key: string): boolean =>
    rows.find((r) => r.role === role)?.permissions.find((p) => p.key === key)?.granted ?? false;

  const tipos = (activeOrganization?.types ?? []) as OrganizationType[];
  const secoes = gruposPorEscopo(tipos);
  const aviso = avisoDeEscopoOculto(tipos);

  return (
    <div className="space-y-5 px-6 py-5">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
          Permissões por papel
        </h3>
        <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
          Escolha o que cada papel pode acessar. Proprietário sempre tem acesso completo — não
          aparece aqui porque não é configurável.
        </p>
      </div>

      {permissionsQuery.isLoading ? (
        <p className="text-sm text-zinc-500 dark:text-muted-foreground">Carregando...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/50 dark:bg-background/50">
              <tr>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                  Recurso
                </th>
                {PERMISSION_ROLE_COLUMNS.map((role) => (
                  <th
                    key={role}
                    className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground"
                  >
                    {ROLE_LABEL[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {secoes.map(({ scope, label: rotuloDaArea, groups }) => (
                <Fragment key={scope}>
                  {/* Cabeçalho da ÁREA (Clínica/Laboratório/Geral) — um nível acima do recurso. */}
                  <tr className="border-t border-zinc-200 dark:border-border">
                    <td
                      colSpan={PERMISSION_ROLE_COLUMNS.length + 1}
                      className="bg-brand-50/60 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-brand-800 dark:bg-primary/10 dark:text-primary"
                    >
                      {rotuloDaArea}
                    </td>
                  </tr>
                  {groups.map((group) => (
                <Fragment key={group.resource}>
                  <tr className="border-t border-zinc-100 dark:border-border/60">
                    <td
                      colSpan={PERMISSION_ROLE_COLUMNS.length + 1}
                      className="bg-zinc-50/70 px-3 py-1.5 text-[12px] font-semibold text-zinc-700 dark:bg-background/30 dark:text-foreground/80"
                    >
                      {group.label}
                    </td>
                  </tr>
                  {group.keys.map(({ key, label }) => (
                    <tr key={key} className="border-t border-zinc-100 dark:border-border/60">
                      <td className="px-3 py-2 pl-6 text-zinc-700 dark:text-foreground/80">{label}</td>
                      {PERMISSION_ROLE_COLUMNS.map((role) => (
                        <td key={role} className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isGranted(role, key)}
                            disabled={setPermission.isPending}
                            onChange={(e) =>
                              setPermission.mutate({ role, permissionKey: key, granted: e.target.checked })
                            }
                            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aviso && (
        <p className="text-[12px] text-zinc-500 dark:text-muted-foreground">{aviso}</p>
      )}

      <CustomRolesSection />
    </div>
  );
}
